<template>
    <div class="live-page okaloosa-page">
        <div class="case-study">
            <h1>Okaloosa Island Pier Underwater Camera</h1>

<p class="meta"><strong>Destin–Fort Walton Beach, Florida, USA</strong></p>
<p><strong>System:</strong> Octopus™ Pan-Tilt-Zoom Underwater Observatory</p>
<p><strong>Application:</strong> Tourism, Public Engagement, Education & Marine Observation</p>

            <div class="pink-line"></div>

            <div class="deployment-scene" aria-hidden="true">
                <MotionMedia :src="previewVideo" />
            </div>

<section class="deployment-live">
    <h2>Watch Live</h2>

    <div class="video-container">
        <iframe
            :key="$route.fullPath"
            loading="lazy"
            width="960"
            height="540"
            src="https://www.youtube.com/embed/f56BJHhGkUI"
            title="Okaloosa Island Pier"
            frameborder="0"
            allowfullscreen>
        </iframe>
    </div>
</section>

<section>
    <h2>Project Overview</h2>

    <p>
        Located at the iconic Okaloosa Island Pier along Florida's Emerald Coast,
        this live underwater observatory provides residents and visitors with a
        real-time view of the vibrant marine ecosystem beneath the Gulf waters.
    </p>

    <p>
        The project was developed through a partnership with Destin-Fort Walton Beach
        and Okaloosa Island Pier to create a unique educational and tourism experience
        that showcases the area's diverse marine life.
    </p>

    <p>
        Through a live-streaming Fathom Ocean Octopus™ system, viewers can observe fish,
        sea turtles, dolphins, rays, and other marine species that inhabit the waters
        surrounding the pier.
    </p>
</section>

<div class="pink-line"></div>

<section>
    <h2>The Challenge</h2>

    <p>
        Destin-Fort Walton Beach sought an innovative way to connect the public with
        the marine environment while promoting environmental awareness and enhancing
        the visitor experience.
    </p>

    <p>
        The project required a reliable underwater camera capable of operating long-term
        in a challenging marine environment while providing continuous public access to
        live underwater footage.
    </p>
</section>

<div class="pink-line"></div>

<section>
    <h2>The Fathom Ocean Solution</h2>

    <p>
        Fathom Ocean deployed an Octopus™ Pan-Tilt-Zoom underwater observatory system
        at the end of the Okaloosa Island Pier.
    </p>

    <p><strong>Key system capabilities include:</strong></p>

    <ul>
        <li>Remote pan-tilt-zoom control</li>
        <li>Self-cleaning optics</li>
        <li>Continuous live streaming</li>
        <li>Long-term underwater deployment</li>
        <li>Public viewing access</li>
        <li>Tourism and educational applications</li>
    </ul>

    <p>
        The system's self-cleaning technology helps maintain clear visibility while
        reducing maintenance requirements, ensuring viewers can enjoy uninterrupted
        access to the underwater world.
    </p>
</section>

<div class="pink-line"></div>

<section>
    <h2>Impact & Results</h2>

    <p>
        The Okaloosa Island Pier Underwater Camera has become a valuable community and
        tourism resource, providing a new perspective on the Emerald Coast's marine environment.
    </p>

    <p><strong>Project outcomes include:</strong></p>

    <ul>
        <li>Real-time viewing of Gulf marine life</li>
        <li>Increased public awareness of local ecosystems</li>
        <li>Educational opportunities for students and families</li>
        <li>Enhanced visitor engagement</li>
        <li>A unique tourism attraction for the Destin-Fort Walton Beach region</li>
    </ul>

    <p>
        The camera regularly captures sea turtles, dolphins, rays, schools of fish,
        and other species that utilize the pier as a thriving artificial reef habitat.
    </p>
</section>

<div class="pink-line"></div>

<section>
    <h2>Why This Project Matters</h2>

    <p>
        The Okaloosa Island Pier Underwater Camera demonstrates how live underwater
        observatories can connect communities with the marine environments that support
        local economies, recreation, and conservation efforts.
    </p>

    <p>
        By making marine life visible and accessible to anyone with an internet connection,
        the project encourages environmental stewardship while creating an engaging experience
        for residents, visitors, classrooms, and ocean enthusiasts around the world.
    </p>
</section>

<div class="pink-line"></div>

<section>
    <h2>System Configuration</h2>

    <ul>
        <li>Octopus™ PTZ Underwater Observatory</li>
        <li>Self-cleaning technology</li>
        <li>Remote operation</li>
        <li>Continuous livestreaming</li>
        <li>Long-term deployment</li>
        <li>Tourism and public engagement focused installation</li>
    </ul>
</section>

<div class="pink-line"></div>

<section>
    <h2>Interested in a Similar Project?</h2>

    <p>
        Whether you're a tourism organization, marina, pier operator, waterfront destination,
        municipality, or educational facility, Fathom Ocean can help create a live underwater
        experience that engages visitors and showcases the marine world below the surface.
    </p>

    <b-button @click="$router.push('/contact')">
        Schedule a Consultation
    </b-button>
</section>
        </div>
    </div>
</template>

<script>
import MotionMedia from '@/components/journey/MotionMedia.vue'
import previewVideo from '@/assets/videos/okaloosaislandpier.mp4'

export default {
    name: 'OkaloosaIslandPier',
    components: {
        MotionMedia,
    },
    data() {
        return {
            previewVideo,
        }
    },
}
</script>
